"use client";

/**
 * ApprovalToolDisplay - Human-in-the-loop approval UI
 *
 * Displays approval request for destructive or sensitive operations.
 * Requires user confirmation before proceeding.
 */

import React, { useState } from "react";
import { Card, Tag, Alert, Button } from "@/ui";
import { colors, spacing, radius, textStyles, primitives } from "@/ui/tokens";
import {
  Warning,
  CheckCircle,
  Cancel,
  ExpandMore,
  ExpandLess,
  Delete,
  Email,
  LinkOff,
  Help,
} from "@mui/icons-material";
import {
  ToolCard,
  ToolHeader,
  ToolBody,
  ToolDivider,
} from "../../Tool";
import type { AggregatedToolCall } from "@/lib/sola/types/tools";

export interface ApprovalToolDisplayProps {
  toolCall: AggregatedToolCall;
  onApprove: () => void;
  onDeny: () => void;
  isProcessing?: boolean;
}

/**
 * Tool-specific icons and descriptions for approval UI
 */
const TOOL_CONFIG: Record<
  string,
  {
    icon: React.ReactNode;
    title: string;
    description: string;
    severity: "warning" | "error";
    confirmLabel: string;
  }
> = {
  sendEmailCampaign: {
    icon: <Email />,
    title: "Send Email Campaign",
    description:
      "This will send emails to the selected recipients. This action cannot be undone.",
    severity: "warning",
    confirmLabel: "Send Emails",
  },
  deleteCandidate: {
    icon: <Delete />,
    title: "Delete Candidate",
    description:
      "This will permanently delete the candidate and all associated data. This action cannot be undone.",
    severity: "error",
    confirmLabel: "Delete Candidate",
  },
  deleteContact: {
    icon: <Delete />,
    title: "Delete Contact",
    description:
      "This will permanently delete the contact and all associated data. This action cannot be undone.",
    severity: "error",
    confirmLabel: "Delete Contact",
  },
  deleteFloat: {
    icon: <Delete />,
    title: "Delete Float",
    description:
      "This will permanently delete the float/job posting. This action cannot be undone.",
    severity: "error",
    confirmLabel: "Delete Float",
  },
  disconnectIntegration: {
    icon: <LinkOff />,
    title: "Disconnect Integration",
    description:
      "This will disconnect the integration and may affect synced data. You can reconnect later.",
    severity: "warning",
    confirmLabel: "Disconnect",
  },
};

const DEFAULT_CONFIG = {
  icon: <Help />,
  title: "Action Requires Approval",
  description: "This action requires your approval before it can proceed.",
  severity: "warning" as const,
  confirmLabel: "Approve",
};

/**
 * Format tool input parameters for display
 */
function formatInputParams(input: unknown): React.ReactNode {
  if (!input || typeof input !== "object") return null;

  const params = input as Record<string, unknown>;
  const displayParams: Array<{ key: string; value: string }> = [];

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;

    let displayValue: string;
    if (typeof value === "object") {
      displayValue = JSON.stringify(value);
    } else {
      displayValue = String(value);
    }

    // Skip internal fields
    if (key.startsWith("_") || key === "confirm") continue;

    displayParams.push({
      key: key.replace(/_/g, " ").replace(/([A-Z])/g, " $1").trim(),
      value: displayValue.length > 100 ? displayValue.substring(0, 100) + "..." : displayValue,
    });
  }

  if (displayParams.length === 0) return null;

  return (
    <div style={{ marginTop: spacing[4] }}>
      <span style={{ ...textStyles.caption, color: colors.text.muted, fontWeight: 600 }}>
        Parameters:
      </span>
      <div style={{ display: "flex", flexDirection: "column", gap: spacing[1], marginTop: spacing[2] }}>
        {displayParams.map(({ key, value }) => (
          <div
            key={key}
            style={{
              display: "flex",
              gap: spacing[2],
              alignItems: "baseline",
              ...textStyles.body2,
            }}
          >
            <span
              style={{
                ...textStyles.caption,
                fontWeight: 500,
                textTransform: "capitalize",
                minWidth: 80,
              }}
            >
              {key}:
            </span>
            <span
              style={{
                ...textStyles.caption,
                color: colors.text.muted,
                wordBreak: "break-word",
              }}
            >
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ApprovalToolDisplay({
  toolCall,
  onApprove,
  onDeny,
  isProcessing = false,
}: ApprovalToolDisplayProps) {
  const [showDetails, setShowDetails] = useState(false);

  const config = TOOL_CONFIG[toolCall.toolName] || DEFAULT_CONFIG;
  const isError = config.severity === "error";

  return (
    <ToolCard status="executing" noPadding>
      <ToolHeader
        title={config.title}
        toolName={toolCall.toolName}
        status="executing"
        icon={<Warning style={{ fontSize: 18, color: isError ? colors.signal.danger : colors.signal.warning }} />}
      />

      <ToolBody isOpen noPadding>
        {/* Warning Alert */}
        <Alert
          variant={config.severity === "error" ? "error" : "warning"}
          title="Approval Required"
        >
          {config.description}
        </Alert>

        {/* Input Parameters */}
        <div style={{ padding: spacing[4] }}>
          <div
            onClick={() => setShowDetails(!showDetails)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              padding: spacing[2],
              borderRadius: radius.sm,
            }}
          >
            <span style={{ ...textStyles.body2, fontWeight: 600 }}>
              Action Details
            </span>
            <button
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: spacing[1],
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: radius.sm,
                color: colors.text.primary,
              }}
            >
              {showDetails ? <ExpandLess /> : <ExpandMore />}
            </button>
          </div>

          {showDetails && (
            <Card
              variant="outlined"
              padding="sm"
              style={{
                borderRadius: radius.md,
                backgroundColor: primitives.neutral[50],
                marginTop: spacing[2],
              }}
            >
              <div style={{ display: "flex", gap: spacing[2], marginBottom: spacing[2] }}>
                <Tag variant="default" size="sm" color="neutral">
                  {toolCall.toolName}
                </Tag>
                <Tag variant="default" size="sm" color="neutral">
                  ID: {toolCall.toolCallId.substring(0, 8)}...
                </Tag>
              </div>
              {formatInputParams(toolCall.input)}
            </Card>
          )}
        </div>

        <ToolDivider />

        {/* Action Buttons */}
        <div
          style={{
            padding: spacing[4],
            display: "flex",
            gap: spacing[4],
            justifyContent: "flex-end",
            backgroundColor: primitives.neutral[50],
          }}
        >
          <Button
            variant="secondary"
            startIcon={<Cancel />}
            onClick={onDeny}
            disabled={isProcessing}
          >
            Deny
          </Button>
          <Button
            variant="primary"
            startIcon={<CheckCircle />}
            onClick={onApprove}
            disabled={isProcessing}
            style={{
              backgroundColor: isError ? colors.signal.danger : colors.signal.warning,
            }}
          >
            {isProcessing ? "Processing..." : config.confirmLabel}
          </Button>
        </div>
      </ToolBody>
    </ToolCard>
  );
}

/**
 * Approval responded display - shows outcome
 */
export function ApprovalRespondedDisplay({
  toolCall,
  approved,
}: {
  toolCall: AggregatedToolCall;
  approved: boolean;
}) {
  const config = TOOL_CONFIG[toolCall.toolName] || DEFAULT_CONFIG;

  return (
    <ToolCard status={approved ? "complete" : "error"} noPadding>
      <ToolHeader
        title={config.title}
        toolName={toolCall.toolName}
        status={approved ? "complete" : "error"}
        icon={approved ? <CheckCircle style={{ fontSize: 18 }} /> : <Cancel style={{ fontSize: 18 }} />}
      />

      <ToolBody isOpen noPadding>
        <Alert
          variant={approved ? "success" : "info"}
          title={approved ? "Approved" : "Denied"}
        >
          {approved
            ? "The action has been approved and is being executed."
            : "The action was denied and will not be executed."}
        </Alert>
      </ToolBody>
    </ToolCard>
  );
}

export default ApprovalToolDisplay;
